import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

// Grid icons
import multiLocationIcon from "../../client/icons/grid/multilocation.png";
import accessControlIcon from "../../client/icons/grid/accesscontrol.png";
import noticesIcon from "../../client/icons/grid/notices.png";
import eventsIcon from "../../client/icons/grid/events.png";
import directoryIcon from "../../client/icons/grid/directory.png";
import lostFoundIcon from "../../client/icons/grid/lostandfound.png";
import marketplaceIcon from "../../client/icons/grid/marketplace.png";
import facilitiesIcon from "../../client/icons/grid/Facilities.png";
import foodCourtIcon from "../../client/icons/grid/foodcourt.png";

interface Feature {
  name: string;
  description: string;
  icon: string;
}

const features: Feature[] = [
  {
    name: "Building Guide",
    description:
      "A complete directory of university buildings and facilities with detailed info and services.",
    icon: multiLocationIcon,
  },
  {
    name: "Student Clubs",
    description:
      "Discover and join student-led organizations that match your interests and passions.",
    icon: accessControlIcon,
  },
  {
    name: "Announcements",
    description: "Get critical updates from faculty and campus administration in real-time.",
    icon: noticesIcon,
  },
  {
    name: "Campus Events",
    description: "Stay in the loop with workshops, guest speakers, and student union mixers.",
    icon: eventsIcon,
  },
  {
    name: "Research Labs",
    description: "Connect with faculty and peers for revolutionary research initiatives.",
    icon: directoryIcon,
  },
  {
    name: "Study Groups",
    description: "Find study partners and book quiet rooms across various libraries.",
    icon: facilitiesIcon,
  },
  {
    name: "Sports & Fitness",
    description: "Access intramural sports leagues and real-time gym occupancy stats.",
    icon: marketplaceIcon,
  },
  {
    name: "Dining & Menus",
    description: "Check daily menus and opening hours for all campus eateries.",
    icon: foodCourtIcon,
  },
  {
    name: "Health Services",
    description: "Easily book appointments with campus health and wellness professionals.",
    icon: lostFoundIcon,
  },
];

export default function FeaturesGrid() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="features" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-4">
            Campus Ecosystem
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Everything in one place
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            A comprehensive suite of tools designed to enhance every aspect of your
            university experience, from academics to leisure.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.name}
              feature={feature}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface FeatureCardProps {
  feature: Feature;
  index: number;
  isVisible: boolean;
}

function FeatureCard({ feature, index, isVisible }: FeatureCardProps) {
  return (
    <div
      className={cn(
        "group relative p-6 rounded-2xl bg-card border border-border/50 transition-all duration-500 hover:border-primary/30 hover:shadow-xl hover:-translate-y-1",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
      )}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      {/* Icon image */}
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110">
        <img
          src={feature.icon}
          alt={feature.name}
          className="w-12 h-12 object-contain"
        />
      </div>

      {/* Content */}
      <h3 className="text-lg font-semibold mb-2">{feature.name}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {feature.description}
      </p>

      {/* Hover gradient overlay */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none bg-gradient-to-br from-current to-transparent" />
    </div>
  );
}
